import React, { useMemo, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useSubscriptions } from '@/features/account/hooks/useSubscriptions'
import { useActiveTier } from '@/features/account/hooks/useActiveTier'

/** Paths allowed when user has no subscription (org, user, subscriptions only). */
const ALLOWED_WITHOUT_SUBSCRIPTION = ['/subscriptions', '/organization', '/user']

/**
 * Guard that redirects to the subscriptions page when the user has no subscription,
 * instead of showing a modal. Only /subscriptions, /organization, /user are allowed
 * without a subscription; other routes redirect to /subscriptions.
 */
const SimpleOnboardingGuard = () => {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { organizationSubscriptions, loading } = useSubscriptions()
  const { activeTier, isDemoMode } = useActiveTier()

  const hasNoSubscription = useMemo(() => {
    const subs = organizationSubscriptions || []
    if (subs.length === 0) return true
    return subs.length > 0 && !activeTier && !isDemoMode
  }, [organizationSubscriptions, activeTier, isDemoMode])

  useEffect(() => {
    if (loading || !hasNoSubscription) return
    const allowed = ALLOWED_WITHOUT_SUBSCRIPTION.some(
      allowedPath => pathname === allowedPath || pathname.startsWith(`${allowedPath}/`)
    )
    if (!allowed) {
      navigate('/subscriptions', { replace: true })
    }
  }, [loading, hasNoSubscription, pathname, navigate])

  return null
}
SimpleOnboardingGuard.displayName = 'SimpleOnboardingGuard'

export default SimpleOnboardingGuard
